import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  GraduationCap, Check, Loader2, UserMinus, UserPlus, ArrowLeftRight,
  History, Bot, FileDown, Users, Sparkles, X, Plus, BookOpen, CalendarClock,
} from "lucide-react";

const KIND_META = {
  departure: { label: "Departure", icon: UserMinus, cls: "text-red-400 border-red-500/30 bg-red-500/5" },
  promotion: { label: "Promotion", icon: UserPlus, cls: "text-emerald-400 border-emerald-500/30 bg-emerald-500/5" },
  role_change: { label: "Role change", icon: ArrowLeftRight, cls: "text-sky-400 border-sky-500/30 bg-sky-500/5" },
};

function ScoreBar({ score }) {
  const color = score >= 70 ? "bg-emerald-500" : score >= 40 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-3">
      <div className="h-2 flex-1 rounded-full bg-secondary overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-sm font-mono w-10 text-right" data-testid="induction-score-value">{score}%</span>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
    completed: "text-sky-400 border-sky-500/30 bg-sky-500/10",
    cancelled: "text-muted-foreground border-border bg-secondary/40",
  };
  return (
    <span className={`text-[10px] font-mono uppercase tracking-wider border rounded-full px-2 py-0.5 ${map[status] || map.cancelled}`}>
      {status}
    </span>
  );
}

export default function Induction() {
  const { isAdmin } = useAuth();
  const [mine, setMine] = useState(null);
  const [all, setAll] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ predecessor_user_id: "", successor_user_id: "", kind: "departure", buddy_user_id: "", shadow_mode: true });

  const load = useCallback(async () => {
    try {
      const [m, list, ppl] = await Promise.all([
        api.get("/succession/mine"),
        api.get("/succession"),
        api.get("/memory/people"),
      ]);
      setMine(m.data);
      setAll(list.data);
      setPeople(ppl.data);
    } catch {
      toast.error("Failed to load induction data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll while the Claude brief is still generating.
  useEffect(() => {
    if (mine?.active && !mine.succession?.brief_ready) {
      const t = setTimeout(load, 5000);
      return () => clearTimeout(t);
    }
  }, [mine, load]);

  const toggleItem = async (key) => {
    try {
      const { data } = await api.post(`/succession/${mine.succession.id}/milestones/${key}`);
      // Apply the returned succession immediately — no full reload race.
      setMine((prev) => (prev?.active ? { ...prev, succession: data.succession } : prev));
      setAll((prev) => prev.map((r) => (r.id === data.succession.id ? data.succession : r)));
      toast.success(`Induction progress: ${data.completion_score}%`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update milestone");
    }
  };

  const toggleShadow = async (sid, enabled) => {
    try {
      await api.post(`/succession/${sid}/shadow`, { enabled });
      toast.success(`Shadow mode ${enabled ? "on" : "off"}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not toggle shadow mode");
    }
  };

  const cancel = async (sid) => {
    if (!window.confirm("Cancel this induction? Milestone progress will be kept as history.")) return;
    try {
      await api.delete(`/succession/${sid}`);
      toast.success("Induction cancelled");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not cancel");
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (creating) return;
    setCreating(true);
    try {
      await api.post("/succession", {
        predecessor_user_id: form.predecessor_user_id,
        successor_user_id: form.successor_user_id,
        kind: form.kind,
        buddy_user_id: form.buddy_user_id || null,
        shadow_mode: form.shadow_mode,
      });
      toast.success("Induction started — Day-1 brief is being generated");
      setShowCreate(false);
      setForm({ predecessor_user_id: "", successor_user_id: "", kind: "departure", buddy_user_id: "", shadow_mode: true });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create induction");
    } finally {
      setCreating(false);
    }
  };

  const downloadPack = async (packId, fmt) => {
    try {
      const { data, headers } = await api.get(`/memory/handover/${packId}/download`, { params: { fmt }, responseType: "blob" });
      const fname = ((headers["content-disposition"] || "").split("filename=")[1] || `handover.${fmt}`).replace(/"/g, "");
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const s = mine?.active ? mine.succession : null;
  const brief = s?.brief;
  const selectCls = "h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-ring";

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="induction-page">
      <header className="flex items-start justify-between gap-4 mb-8">
        <div>
          <p className="eyebrow mb-2">Employee induction</p>
          <h1 className="font-display text-2xl">Day-1 dashboard &amp; role succession</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Predecessor memory, 30/60/90 milestones, shadow mode, and your buddy bot.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowCreate(true)} className="rounded-full shrink-0" data-testid="induction-create-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Start induction
          </Button>
        )}
      </header>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          {/* ===== My induction ===== */}
          {s ? (
            <div className="space-y-4 mb-10">
              {/* Hero card */}
              <div className="bento p-7" data-testid="induction-hero">
                <div className="flex flex-wrap items-start justify-between gap-6">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      {(() => { const km = KIND_META[s.kind] || KIND_META.departure; const KIcon = km.icon; return (
                        <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider border rounded-full px-2.5 py-1 ${km.cls}`}>
                          <KIcon className="w-3 h-3" /> {km.label}
                        </span>
                      ); })()}
                      <span className="text-xs text-muted-foreground">
                        succeeding <span className="text-foreground">{s.predecessor?.name || s.predecessor?.email}</span>
                      </span>
                    </div>
                    <p className="font-display text-4xl tracking-tight" data-testid="induction-day-count">
                      Day {mine.days_since_start} <span className="text-muted-foreground text-2xl">of 90</span>
                    </p>
                    <div className="mt-4 w-72 max-w-full" data-testid="induction-score-bar">
                      <ScoreBar score={s.completion_score} />
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Button
                      size="sm"
                      variant={s.shadow_mode ? "default" : "outline"}
                      className="rounded-full"
                      onClick={() => toggleShadow(s.id, !s.shadow_mode)}
                      data-testid="induction-shadow-toggle"
                    >
                      <History className="w-3.5 h-3.5 mr-1.5" />
                      Shadow mode {s.shadow_mode ? "on" : "off"}
                    </Button>
                    <p className="text-[11px] text-muted-foreground max-w-[220px] text-right">
                      {s.shadow_mode
                        ? `Your chats include a second answer from ${s.predecessor?.name || "your predecessor"}'s preserved memory.`
                        : "Shadow mode off — you're flying solo."}
                    </p>
                  </div>
                </div>
              </div>

              {/* Buddy + predecessor memory */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bento p-6" data-testid="induction-buddy-card">
                  <div className="flex items-center gap-2 mb-3">
                    <Bot className="w-4 h-4 text-emerald-400" />
                    <p className="eyebrow">Your buddy bot</p>
                  </div>
                  <p className="font-display text-xl">{s.buddy?.name || s.buddy?.email || "—"}</p>
                  <p className="text-sm text-muted-foreground mt-1 mb-4">
                    {s.buddy_auto ? "Auto-assigned · richest memory in the workspace" : "Assigned by your admin"}
                  </p>
                  <Link to={`/app/chat?prefill=${encodeURIComponent("/buddy ")}`}>
                    <Button size="sm" variant="outline" className="rounded-full" data-testid="induction-buddy-ask">
                      Ask via /buddy <Sparkles className="w-3.5 h-3.5 ml-1.5" />
                    </Button>
                  </Link>
                </div>

                <div className="bento p-6" data-testid="induction-predecessor-card">
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen className="w-4 h-4 text-sky-400" />
                    <p className="eyebrow">{s.kind === "promotion" ? "Role history (read-only)" : "Predecessor's memory"}</p>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    <span className="text-foreground font-medium">{mine.predecessor_counts.decisions}</span> decisions ·{" "}
                    <span className="text-foreground font-medium">{mine.predecessor_counts.processes}</span> processes ·{" "}
                    <span className="text-foreground font-medium">{mine.predecessor_counts.total}</span> total entries
                  </p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Link to="/app/memory">
                      <Button size="sm" variant="outline" className="rounded-full" data-testid="induction-open-memory">
                        Open memory timeline
                      </Button>
                    </Link>
                    {mine.handover_pack && (
                      <Button size="sm" className="rounded-full" onClick={() => downloadPack(mine.handover_pack.id, "pdf")} data-testid="induction-download-pack">
                        <FileDown className="w-3.5 h-3.5 mr-1.5" /> Handover pack
                        {mine.handover_pack.coverage_score != null && ` · ${mine.handover_pack.coverage_score}%`}
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Day-1 brief (Claude) */}
              <div className="bento p-7" data-testid="induction-brief">
                <div className="flex items-center gap-2 mb-5">
                  <GraduationCap className="w-4 h-4 text-emerald-400" />
                  <p className="eyebrow">Day-1 brief · generated by Claude</p>
                </div>
                {!s.brief_ready ? (
                  <p className="text-sm text-muted-foreground flex items-center gap-2" data-testid="induction-brief-loading">
                    <Loader2 className="w-4 h-4 animate-spin" /> Generating your brief from the preserved memory…
                  </p>
                ) : brief ? (
                  <div className="space-y-6">
                    <p className="text-sm leading-relaxed text-muted-foreground max-w-3xl" data-testid="induction-brief-summary">{brief.summary}</p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                      {[
                        { h: "Key decisions", items: brief.decisions },
                        { h: "Processes owned", items: brief.processes },
                        { h: "Open items", items: brief.open_items },
                        { h: "Stakeholders", items: brief.stakeholders },
                      ].map((col) => (
                        <div key={col.h}>
                          <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-mono mb-2.5">{col.h}</p>
                          <ul className="space-y-2">
                            {(col.items || []).length === 0 && <li className="text-xs text-muted-foreground/60 italic">None recorded</li>}
                            {(col.items || []).map((it, i) => (
                              <li key={i} className="text-xs leading-relaxed border-l-2 border-border pl-2.5">{it}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">Brief generation didn't complete — milestone tracking still works.</p>
                )}
              </div>

              {/* Milestones */}
              <div className="grid md:grid-cols-3 gap-4" data-testid="induction-milestones">
                {mine.milestones_template.map((phase) => (
                  <div key={phase.key} className="bento p-6">
                    <p className="font-display text-lg mb-4 flex items-center gap-2">
                      <CalendarClock className="w-4 h-4 text-muted-foreground" /> {phase.label}
                    </p>
                    <ul className="space-y-2.5">
                      {phase.items.map((item) => {
                        const prog = (s.milestones || {})[item.key] || {};
                        const done = !!prog.done;
                        const autoCount = item.auto === "shadow_questions" ? s.shadow_questions
                          : item.auto === "buddy_sessions" ? s.buddy_sessions : null;
                        return (
                          <li key={item.key}>
                            <button
                              onClick={() => toggleItem(item.key)}
                              className="w-full flex items-start gap-2.5 text-left group"
                              data-testid={`induction-milestone-${item.key}`}
                            >
                              <span className={`mt-0.5 w-[18px] h-[18px] rounded border flex items-center justify-center shrink-0 transition-colors ${
                                done ? "bg-emerald-500 border-emerald-500 text-white" : "border-border group-hover:border-foreground/50"
                              }`}>
                                {done && <Check className="w-3 h-3" />}
                              </span>
                              <span className={`text-xs leading-relaxed ${done ? "text-muted-foreground line-through" : ""}`}>
                                {item.label}
                                {autoCount != null && (
                                  <span className="block text-[10px] font-mono text-muted-foreground mt-0.5">
                                    auto-tracked · {autoCount || 0}/{item.target}
                                  </span>
                                )}
                                {prog.done_by === "auto" && done && (
                                  <span className="block text-[10px] font-mono text-emerald-500 mt-0.5">auto-completed</span>
                                )}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bento p-10 text-center mb-10" data-testid="induction-empty">
              <GraduationCap className="w-8 h-8 mx-auto mb-4 text-muted-foreground" />
              <p className="font-display text-xl mb-2">No active induction</p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                {isAdmin
                  ? "Start an induction when someone departs, is promoted, or changes role — their successor inherits the memory context."
                  : "When an admin assigns you a succession, your Day-1 dashboard will appear here."}
              </p>
            </div>
          )}

          {/* ===== Admin: all successions ===== */}
          {isAdmin && (
            <section data-testid="induction-admin-section">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-4 h-4 text-muted-foreground" />
                <h2 className="font-display text-lg">All successions</h2>
              </div>
              {all.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="induction-list-empty">No successions yet.</p>
              ) : (
                <div className="space-y-2" data-testid="induction-list">
                  {all.map((r) => (
                    <div key={r.id} className="bento p-4 flex flex-wrap items-center gap-4" data-testid={`induction-row-${r.id}`}>
                      <div className="flex-1 min-w-[220px]">
                        <p className="text-sm">
                          <span className="text-muted-foreground">{r.predecessor?.name || r.predecessor?.email}</span>
                          <span className="mx-2 text-muted-foreground">→</span>
                          <span className="font-medium">{r.successor?.name || r.successor?.email}</span>
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          {(KIND_META[r.kind] || KIND_META.departure).label} · started {new Date(r.start_date).toLocaleDateString()}
                          {r.buddy && ` · buddy: ${r.buddy.name || r.buddy.email}`}
                        </p>
                      </div>
                      <div className="w-40"><ScoreBar score={r.completion_score} /></div>
                      <StatusBadge status={r.status} />
                      {r.status === "active" && (
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="rounded-full h-8 text-xs"
                            onClick={() => toggleShadow(r.id, !r.shadow_mode)} data-testid={`induction-shadow-admin-${r.id}`}>
                            Shadow {r.shadow_mode ? "on" : "off"}
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-full h-8 text-xs text-destructive border-destructive/30 hover:bg-destructive/10"
                            onClick={() => cancel(r.id)} data-testid={`induction-cancel-${r.id}`}>
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </>
      )}

      {/* ===== Create modal ===== */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="induction-create-modal">
          <div className="bento w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-lg">Start an induction</h3>
              <button onClick={() => setShowCreate(false)} className="text-muted-foreground hover:text-foreground" data-testid="induction-create-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={create} className="space-y-4">
              <div>
                <label className="eyebrow block mb-1.5">Predecessor (whose memory is inherited)</label>
                <select required className={selectCls} value={form.predecessor_user_id}
                  onChange={(e) => setForm({ ...form, predecessor_user_id: e.target.value })} data-testid="induction-select-predecessor">
                  <option value="" disabled>Select person</option>
                  {people.map((p) => <option key={p.user_id} value={p.user_id}>{p.name || p.email}</option>)}
                </select>
              </div>
              <div>
                <label className="eyebrow block mb-1.5">Successor (new in role)</label>
                <select required className={selectCls} value={form.successor_user_id}
                  onChange={(e) => setForm({ ...form, successor_user_id: e.target.value })} data-testid="induction-select-successor">
                  <option value="" disabled>Select person</option>
                  {people.map((p) => <option key={p.user_id} value={p.user_id}>{p.name || p.email}</option>)}
                </select>
              </div>
              <div>
                <label className="eyebrow block mb-1.5">Kind</label>
                <select className={selectCls} value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="induction-kind-select">
                  <option value="departure">Departure — auto-generates handover pack</option>
                  <option value="promotion">Promotion — predecessor memory becomes role history</option>
                  <option value="role_change">Role change</option>
                </select>
              </div>
              <div>
                <label className="eyebrow block mb-1.5">Buddy bot</label>
                <select className={selectCls} value={form.buddy_user_id}
                  onChange={(e) => setForm({ ...form, buddy_user_id: e.target.value })} data-testid="induction-select-buddy">
                  <option value="">Auto-suggest (richest memory)</option>
                  {people.map((p) => <option key={p.user_id} value={p.user_id}>{p.name || p.email}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2.5 text-sm cursor-pointer">
                <input type="checkbox" checked={form.shadow_mode}
                  onChange={(e) => setForm({ ...form, shadow_mode: e.target.checked })}
                  className="accent-emerald-500 w-4 h-4" data-testid="induction-shadow-checkbox" />
                Shadow mode — dual answers from the predecessor's memory
              </label>
              <Button type="submit" className="rounded-full w-full" disabled={creating} data-testid="induction-submit-btn">
                {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting…</> : "Start induction"}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
